import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState } from 'react';

import {
  List,
  Card,
  Stack,
  Select,
  Avatar,
  Button,
  ListItem,
  MenuItem,
  useTheme,
  TextField,
  Typography,
  InputLabel,
  FormControl,
  ListItemAvatar,
  InputAdornment,
} from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import Iconify from 'src/components/iconify';

import { scrollbarStyle } from 'src/sections/style';
import {
  cardTicketModalStyle,
  listTicketModalStyle,
  listItemTicketModalStyle,
} from 'src/sections/ticketDetails/styles';

import Label from '../../../components/label';
import { useConfirmModal } from '../../../hooks/useModal';
import { getComparator, applySortFilter } from '../../../utils/filterData';

AssignTicketModal.propTypes = {
  usersList: PropTypes.array,
  assignTicket: PropTypes.func,
  department: PropTypes.string,
  statusesList: PropTypes.array,
  creatorId: PropTypes.number,
};

const order = 'asc';
const orderBy = 'name';

export default function AssignTicketModal({
  usersList,
  assignTicket,
  department,
  statusesList,
  creatorId,
}) {
  const [selectedAgent, setSelectedAgent] = useState({
    id: 0,
    username: '',
  });
  const [selectedStatusId, setSelectedStatusId] = useState(2);
  const [filterName, setFilterName] = useState('');
  const [isNotFound, setIsNotFound] = useState(false);
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const filteredUsers = useMemo(() => {
    const listWithoutCreator = (usersList || []).filter((u) => u.id !== creatorId);

    const filteredData = applySortFilter(
      listWithoutCreator,
      getComparator(order, orderBy),
      filterName,
      'username'
    );
    setIsNotFound(!filteredData.length && !!filterName);
    return filteredData;
  }, [filterName, usersList, creatorId]);

  const { showConfirmModal } = useConfirmModal(
    t('pages.assignTicket.confirmAssignment'),
    `${t('pages.assignTicket.areYouSure')} ${selectedAgent.username} ?`,
    async () => {
      assignTicket(selectedAgent.id, selectedStatusId);
    }
  );

  const handleStatusChange = (event) => {
    setSelectedStatusId(event.target.value);
  };

  return (
    <Stack px={2} spacing={2} my={2}>
      <Typography
        variant="subtitle1"
        sx={{
          fontSize: isSmallScreen ? '0.775rem' : '1rem',
        }}
      >
        {t('pages.assignTicket.availableAgents')} {department} :
      </Typography>
      <TextField
        value={filterName}
        onChange={handleFilterByName}
        placeholder={`${t('filters.search')} agent...`}
        size={isSmallScreen ? 'small' : 'medium'}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify
                icon="eva:search-fill"
                sx={{
                  color: 'text.disabled',
                  width: isSmallScreen ? 16 : 20,
                  height: isSmallScreen ? 16 : 20,
                }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          '& input': {
            fontSize: isSmallScreen ? '0.775rem' : '1rem',
          },
        }}
      />
      <Card
        sx={{
          ...cardTicketModalStyle,
          height: 'auto',
          minHeight: isSmallScreen ? '50px' : '100px',
          maxHeight: '350px',
          overflowY: 'auto',
          ...scrollbarStyle,
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <List sx={listTicketModalStyle}>
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => {
              const { id, username, email, phoneNumber, available } = user;

              return (
                <ListItem
                  divider
                  key={id}
                  onClick={() => {
                    setSelectedAgent(user);
                  }}
                  sx={{
                    ...listItemTicketModalStyle(selectedAgent, user),
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: isDark
                        ? 'rgba(255, 255, 255, 0.08) !important'
                        : 'rgba(0, 0, 0, 0.04) !important',
                    },
                  }}
                  alignItems="flex-start"
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    width="100%"
                    justifyContent="space-between"
                  >
                    <Stack direction="row">
                      <ListItemAvatar>
                        <Avatar
                          key={username}
                          src={user.avatarUrl}
                          sx={{
                            width: 40,
                            height: 40,
                            border: isDark
                              ? '1px solid rgba(255,255,255,0.05)'
                              : '1px solid rgba(0,0,0,0.05)',
                          }}
                          imgProps={{ loading: 'lazy' }}
                        />
                      </ListItemAvatar>

                      <Stack spacing={0.5}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: isSmallScreen ? '0.775rem' : '1rem',
                          }}
                        >
                          <b>{username}</b>
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: isSmallScreen ? '0.675rem' : '1rem',
                          }}
                        >
                          <b>{t('attributes.email')}: </b>
                          {email}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: isSmallScreen ? '0.675rem' : '1rem',
                          }}
                        >
                          <b>{t('attributes.phoneNumber')}: </b>
                          {phoneNumber}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Label color={(available && 'success') || 'error'}>
                      {available ? (
                        isSmallScreen ? (
                          <Iconify
                            icon="eva:checkmark-circle-fill"
                            color="success"
                            sx={{ width: 20, height: 20 }}
                          />
                        ) : (
                          t('attributes.available')
                        )
                      ) : isSmallScreen ? (
                        <Iconify
                          icon="eva:close-circle-fill"
                          color="error"
                          sx={{ width: 20, height: 20 }}
                        />
                      ) : (
                        t('attributes.unavailable')
                      )}
                    </Label>
                  </Stack>
                </ListItem>
              );
            })
          ) : (
            <Typography
              textAlign="center"
              variant="subtitle1"
              sx={{
                fontSize: isSmallScreen ? '0.675rem' : '1rem',
              }}
            >
              {isNotFound
                ? t('pages.assignTicket.noAgentsFound')
                : t('pages.assignTicket.noAgentsAvailable')}
            </Typography>
          )}
        </List>
      </Card>
      <FormControl>
        <InputLabel
          id="ticket-status-select-label"
          sx={{
            fontSize: isSmallScreen ? '0.675rem' : '1rem',
          }}
        >
          {t('pages.assignTicket.statusAfterAssign')}
        </InputLabel>
        <Select
          labelId="ticket-status-select-label"
          id="status-select"
          name="status_id"
          label={t('pages.assignTicket.statusAfterAssign')}
          defaultValue={selectedStatusId}
          onChange={handleStatusChange}
          size={isSmallScreen ? 'small' : 'medium'}
          MenuProps={{
            PaperProps: {
              sx: {
                ...scrollbarStyle,
              },
            },
          }}
        >
          {statusesList?.length > 0 ? (
            statusesList.map((status) => {
              const { statusId, name } = status;
              let tName = name;
              if (statusId <= 5) tName = t(`status.${name}`);
              return (
                <MenuItem
                  key={statusId}
                  value={statusId}
                  sx={{
                    fontSize: isSmallScreen ? '0.675rem' : '1rem',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: isSmallScreen ? '0.675rem' : '1rem' }}
                  >
                    {tName}
                  </Typography>
                </MenuItem>
              );
            })
          ) : (
            <MenuItem disabled value="">
              <Typography
                variant="body2"
                sx={{ fontSize: isSmallScreen ? '0.675rem' : '1rem' }}
              >
                {t('infoMessages.noOptions')}
              </Typography>
            </MenuItem>
          )}
        </Select>
      </FormControl>

      <Button
        variant="outlined"
        onClick={selectedAgent.id !== 0 ? showConfirmModal : null}
        disabled={selectedAgent.id === 0}
        sx={{
          fontSize: isSmallScreen ? '0.675rem' : '1rem',
        }}
      >
        {t('pages.assignTicket.assignTicket')}
      </Button>
    </Stack>
  );
}
